import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { TokenContextValue } from "../interfaces/context";
import { getUserInformation, getUserInformationWithIdPartner } from "../services/Api/loginService";
import { checkAuth } from "../services/Api/userService";

const TokenContext = createContext<TokenContextValue | null>(null);

export const TokenProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  
  // Liste des pages publiques où on ne vérifie pas l'authentification
  const publicPages = ['/', '/error', '/legalnotice', '/cookiesnotice', '/cgu', '/resetpassword', '/createaccount', '/workingfolder'];
  const isPublicPage = publicPages.includes(location.pathname);
  const getIdPartnerFromPath = (pathname: string): string | undefined => {
    const partnerMatch = pathname.match(/\/partner\/(\d+)/);
    return partnerMatch ? partnerMatch[1] : undefined;
  };
  const idPartner = getIdPartnerFromPath(location.pathname);
  // state for save user informations
  const [firstname, setFirstname] = useState<string>("");
  const [lastname, setLastname] = useState<string>("");
  const [emailUser, setEmailUser] = useState<string>("");
  const [roleId, setRoleId] = useState<number>(0);
  const [userId, setUserId] = useState<number>(0);
  const [mfa, setMfa] = useState<boolean>(false);
  const [utc, setUtc] = useState<number>(0);
  const [utp, setUtp] = useState<number>(0);
  const [isAdmin, setIsAdmin] = useState<number>(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState<number>(0);
  const [accountType, setAccountType] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  // States pour la gestion de l'authentification
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  const resetUserContext = () => {
    setFirstname("");
    setLastname("");
    setEmailUser("");
    setRoleId(0);
    setUserId(0);
    setMfa(false);
    setUtc(0);
    setUtp(0);
    setIsAdmin(0);
    setIsSuperAdmin(0);
    setAccountType("");
    setTitle("");
    setIsAuthenticated(false);
    setIsLoading(false);
    setHasInitialized(false); // Réinitialiser pour permettre une nouvelle vérification
  };
  
  // Fonction pour récupérer les données utilisateur depuis l'API
  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const userData = idPartner ? await getUserInformationWithIdPartner(Number(idPartner)) : await getUserInformation();

      // Mise à jour des states avec les données de l'API
      setFirstname(userData.data.firstname);
      setLastname(userData.data.lastname);
      setEmailUser(userData.data.eMail);
      setRoleId(userData.data.idRole);
      setUserId(userData.data.userId);
      setMfa(userData.data.mfa);
      setUtc(userData.data.utc);
      setUtp(userData.data.utp);
      setIsAdmin(userData.data.isAdmin);
      setIsSuperAdmin(Number(userData.data.isSuperAdmin));
      setAccountType(userData.data.accountType);
      setTitle(userData.data.title);
      setHasInitialized(true);
      setIsAuthenticated(true);

    } catch (error) {
      setIsAuthenticated(false);
      resetUserContext();
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour vérifier l'authentification et charger les données
  const checkAuthAndLoadData = async () => {
    try {
      setIsLoading(true);
      // Si checkAuth() réussit, cela signifie que l'utilisateur est authentifié
      await checkAuth();
      // Si on arrive ici, l'utilisateur est authentifié, on peut charger ses données
      await fetchUserData();

      // On marque l'utilisateur comme authentifié
      setIsAuthenticated(true);
      setHasInitialized(true);
    } catch (error) {
      // En cas d'erreur (401, etc.), l'utilisateur n'est pas authentifié
      setIsAuthenticated(false);
      setHasInitialized(true); // On marque comme initialisé même en cas d'échec
      resetUserContext();
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour rafraîchir les données utilisateur
  const refreshUserData = async () => {
    // On peut rafraîchir les données même si pas encore authentifié (après connexion)
    await fetchUserData();
  };

  // useEffect pour initialiser l'authentification au montage du composant
  useEffect(() => {
    // Ne pas vérifier l'authentification sur les pages publiques
    if (isPublicPage) {
      setHasInitialized(true);
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }
    
    // Sur les pages protégées, vérifier l'authentification si pas encore fait
    if (!hasInitialized) {
      checkAuthAndLoadData();
    }
  }, [location.pathname]); // Dépendance uniquement sur le changement de route

  return (
    <TokenContext.Provider
      value={{
        firstname,
        setFirstname,
        lastname,
        setLastname,
        emailUser,
        setEmailUser,
        roleId,
        setRoleId,
        userId,
        setUserId,
        mfa,
        setMfa,
        utc,
        setUtc,
        utp,
        setUtp,
        isAdmin,
        setIsAdmin,
        isSuperAdmin,
        setIsSuperAdmin,
        accountType,
        setAccountType,
        title,
        setTitle,
        resetUserContext,
        checkAuthAndLoadData,
        refreshUserData,
        isLoading,
        isAuthenticated,
        hasInitialized,
        setHasInitialized,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};

export const useTokenContext = () => {
  const context = useContext(TokenContext);
  if (context === null) {
    throw new Error("Le contexte doit être utilisé dans un TokenProvider");
  }
  return context;
};
